<template>
  <div>
    <Dialog
      :header="$t('edit-tournament-configuration')"
      :visible.sync="tournamentDialog"
      :style="{ minWidth: '75%', minHeight: '25vh', maxWidth: '95%' }"
      :modal="true"
      :show-header="false"
    >
      <div class="p-mt-3" style="height: 100%; width: 100%; min-height: 50vh">
        <div class="p-my-5" style="height: 5vh">
          <div class="progress-container p-justify-center">
            <ul class="progress-steps">
              <li v-bind:class="{ 'active ': !tournamentAdditionalDialog }">1</li>
              <li>2</li>
            </ul>
          </div>
        </div>

        <h1 class="title is-2 p-my-3">{{ $t("tournament-settings") }}</h1>

        <div class="p-grid">
          <div class="p-col-6" v-bind:class="{ 'p-col-12': !tournamentConfig.division_id }">
            <div class="p-grid">
              <div class="p-col-12">
                <div class="configBox">
                  <div class="p-grid">
                    <div class="p-col-6">
                      <h2 class="title is-4">{{ $t("choose-division") }}</h2>
                      <Dropdown
                        :options="$store.getters.currentSeason.divisions"
                        optionLabel="name"
                        optionValue="id"
                        :placeholder="$t('choose-division')"
                        class="configDown"
                        v-model="tournamentConfig.division_id"
                      />
                    </div>
                    <div class="p-col-6">
                      <h2 class="title is-4">{{ $t("tournament-type") }}</h2>
                      <Dropdown
                        :options="tournamentTypes"
                        optionLabel="name"
                        optionValue="value"
                        :placeholder="$t('choose-type')"
                        class="configDown"
                        v-model="tournamentConfig.elimination"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="p-col-6" v-if="tournamentConfig.division_id">
            <div class="configBox">
              <h2 class="title is-4">{{ $t("set-tournament-dates") }}</h2>
              <div id="datePicker"></div>
              <AirbnbStyleDatepicker
                :sundayFirst="currentUser.first_day === 0"
                :months-to-show="windowWidth > 1000 ? 2 : 1"
                triggerElementId="datePicker"
                :customized-dates="[{ cssClass: 'old', dates: oldDates }]"
                @date-one-selected="setStartDate"
                @date-two-selected="setEndDate"
                :date-one="tournamentConfig.realStart"
                :date-two="tournamentConfig.realEnd"
                mode="range"
                :inline="true"
                :startOpen="true"
              />
            </div>
          </div>

          <div class="p-col-12" v-if="tournamentConfig.division_id">
            <div class="p-my-3" v-if="tournamentConfig.startDate && tournamentConfig.endDate">
              <h2 class="title is-4">{{ $t("review-and-continue") }}</h2>
              <table style="margin: 0 auto; font-size: 1.2em !important" id="needsTable">
                <tr>
                  <th>{{ $t("total-games") }}</th>
                  <th>{{ $t("timeslots-available") }}</th>
                  <th>{{ $t("additional-needed") }}</th>
                </tr>
                <tr>
                  <td>{{ slotsNeeded || "--" }}</td>
                  <td style="background: #eefdec">{{ slotsAvailable(tournamentConfig.division_id) }}</td>
                  <td style="background: #fafbed">{{ additionalNeeded }}</td>
                </tr>
              </table>

              <br />
              <div v-if="additionalNeeded > 0" style="font-size: 1.2em">
                {{
                  $t("youll-need-to-create-additionalneeded-additional-timeslots-to-fulfill-this-schedules-needs", [
                    additionalNeeded,
                  ])
                }}
              </div>
              <div v-else style="color: green; font-size: 1.2em;">
                {{ $t("you-have-enough-timeslots-to-successfully-schedule") }}
              </div>
            </div>
            <div v-if="!tournamentConfig.division_id">{{ $t("please-select-a-division-to-continue") }}</div>
            <div v-else-if="!tournamentConfig.endDate">{{ $t("please-select-schedule-dates-to-continue") }}</div>
            <div v-else-if="!powerOfTwo(teamCount)">{{ $t("number-of-teams-must-be-a-power-of-two-2-4-8-16") }}</div>

            <Button
              style="margin: 15px auto; font-size: 1.5em; width: auto; padding: 10px 25px"
              class="p-button-lg dsButton"
              :disabled="!tournamentValid"
              @click="nextDialog"
            >
              {{ $t("Button.continue") }}
            </Button>
          </div>

          <div class="p-col-12">
            <Button class="p-button-text" @click="cancelTournament">{{ $t("Button.cancel") }}</Button>
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      :header="$t('edit-schedule-configuration')"
      :visible.sync="tournamentAdditionalDialog"
      :modal="true"
      :style="{ minWidth: '75%', minHeight: '25vh', maxWidth: '95%' }"
      :show-header="false"
      :closeOnEscape="false"
    >
      <div class="p-mt-3" style="height: 100%; width: 100%; min-height: 50vh">
        <div class="p-my-5" style="height: 5vh">
          <div class="progress-container p-justify-center">
            <ul class="progress-steps">
              <li>1</li>
              <li v-bind:class="{ 'active ': tournamentAdditionalDialog }">2</li>
            </ul>
          </div>
        </div>

        <div class="p-grid">
          <div class="p-col-12">
            <div class="p-grid p-justify-center">
              <div class="p-col-12">
                <div class="title is-3 p-my-3">
                  <span v-if="additionalNeeded" style="color: darkred;">
                    {{ $t("additionalneeded-more-timeslots-needed", [additionalNeeded]) }}
                  </span>
                  <span v-else style="font-size: 1.2em; color: green; font-weight: bold">
                    <font-awesome-icon
                      class="cursorpointer menuIcon"
                      style="color: green;"
                      :icon="['fal', 'check-circle']"
                    />&nbsp;
                    <span></span>
                  </span>
                </div>
              </div>
              <div class="p-col-6">
                <div class="config-box">
                  <h4 class="title is-4 p-my-3">
                    <div v-if="additionalNeeded > 0" style="font-size: 1.2em">
                      {{
                        $t(
                          "youll-need-to-create-additionalneeded-additional-timeslots-to-fulfill-this-schedules-needs",
                          [additionalNeeded],
                        )
                      }}
                    </div>
                    <div v-else style="font-size: 1.2em; ">
                      {{ $t("you-have-enough-timeslots-to-start-scheduling") }}
                    </div>
                  </h4>
                </div>
              </div>
              <div class="p-col-12">
                <div class="config-box">
                  <Button
                    class="p-button-lg p-mb-2"
                    style="margin: 15px auto; font-size: 1.5em; width: auto; padding: 10px 25px"
                    v-bind:class="{ 'p-button-lg dsButton': additionalNeeded > 0 }"
                    :disabled="!tournamentValid"
                    @click="toggleTournamentDialog(false)"
                  >
                    {{ $t("num-timeslots", [timeslotButton]) }}
                  </Button>

                  <div>
                    <Button class="p-button-sm p-button-text" @click="toggleTournamentConfigDialog(true)">
                      {{ $t("edit-settings") }}
                    </Button>
                  </div>
                </div>
              </div>
              <div class="p-col-12">
                <Button class="p-button-text" @click="cancelTournament"> {{ $t("Button.cancel") }} </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      :header="$t('edit-tournament-configuration')"
      :visible.sync="tournamentScheduleDialog"
      :modal="true"
      :style="{ minWidth: '75%', maxWidth: '75vw' }"
      :show-header="false"
      :closeOnEscape="false"
    >
      <div v-if="scheduleBusy" style="padding: 30px">
        <h1 class="title is-1">{{ $t("scheduling-in-progress") }}</h1>
        <p class="subheader">{{ $t("now-might-be-a-good-time-for-a-water-break") }}</p>
        <br /><br />
        <img class="centeredGif" :src="`${baseURL}/diamond.gif`" />
      </div>
      <div v-else class="p-mt-5" style="">
        <div class="p-my-5" style="height: 5vh">
          <div class="progress-container p-justify-center">
            <ul class="progress-steps">
              <li class="active">1</li>
              <li class="active">2</li>
            </ul>
          </div>
        </div>
        <h1 class="title is-2 p-my-3">{{ $t("submit-tournament") }}</h1>
        <div class="p-grid">
          <div class="p-col-12 p-mb-3 p-mt-1 constraintBox">
            <label class="p-mb-1"> {{ $t("tournament-name") }} </label>
            <br />
            <InputText
              id="scheduleName"
              :placeholder="$t('enter-name')"
              autofocus
              v-model="tournamentConfig.title"
              type="text"
            />
          </div>

          <div class="p-col-12">
            <Button
              style="margin: 15px auto; font-size: 1.5em; height: 79; width: auto; padding: 10px 25px"
              class="p-button-lg dsButton"
              :disabled="!tournamentConfig.title"
              @click="submitTournament"
            >
              {{ $t("Button.submit") }}
            </Button>
            <br /><br />
            <Button class="p-button-text p-button-lg" @click="toggleTournamentDialog(false)">
              {{ $t("Button.cancel") }}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
export default {
  props: [
    "tournamentDialog",
    "tournamentConfig",
    "cancelTournament",
    "setStartDate",
    "setEndDate",
    "windowWidth",
    "oldDates",
    "additionalNeeded",
    "slotsNeeded",
    "slotsAvailable",
    "teamCount",
    "tournamentValid",
    "nextDialog",
    "tournamentAdditionalDialog",
    "tournamentScheduleDialog",
    "toggleTournamentConfigDialog",
    "toggleTournamentDialog",
    "scheduleBusy",
    "submitTournament",
  ],
  data() {
    return {
      tournamentTypes: [
        { name: this.$t("single-elimination"), value: "single" },
        { name: this.$t("double-elimination"), value: "double" },
      ],
    };
  },
  methods: {
    powerOfTwo(x) {
      return (Math.log(x) / Math.log(2)) % 1 === 0;
    },
  },
  computed: {
    timeslotButton() {
      if (this.additionalNeeded > 0) {
        return this.$t("Button.create");
      } else {
        return this.$t("Button.review");
      }
    },
  },
};
</script>
